import { parseConvexIdOf } from "@/lib/looks-like-convex-document-id";
import type { Id } from "@v1/backend/convex/_generated/dataModel";

/** Portal = tenant of a Hausverwaltung; app = staff of our customer, talks to our support. */
export type MessengerSurface = "portal" | "app";

export type MessengerView =
  | { readonly kind: "home" }
  /** „Nachrichten“: chats and tickets in one list. */
  | { readonly kind: "messages" }
  | { readonly kind: "help" }
  /** `null` = new chat, created by the first message (`pendingText` shows it until the server has it). */
  | {
      readonly kind: "chat";
      readonly sessionId: Id<"public_chat_sessions"> | null;
      readonly pendingText?: string;
    }
  | { readonly kind: "ticket"; readonly taskId: Id<"tasks"> }
  | { readonly kind: "helpSpace"; readonly spaceId: string }
  | { readonly kind: "article"; readonly pageId: string };

export type MessengerTab = "home" | "messages" | "help";

export const HOME_VIEW: MessengerView = { kind: "home" };

export function isTabView(view: MessengerView): view is { readonly kind: MessengerTab } {
  return view.kind === "home" || view.kind === "messages" || view.kind === "help";
}

/** Help views open the wide panel on desktop: articles need room to read. */
export function isWideView(view: MessengerView): boolean {
  return view.kind === "help" || view.kind === "helpSpace" || view.kind === "article";
}

/** `?help=` deep link (`start`, `messages`, `ticket/<id>`, `article/<id>` …); null = not a messenger link. */
export function parseMessengerParam(raw: string | null): MessengerView | null {
  if (raw === null) return null;
  const [head, rest] = raw.split("/", 2);
  if (head === "start") return { kind: "home" };
  if (head === "messages" || head === "chats" || head === "tickets") return { kind: "messages" };
  if (head === "help") return { kind: "help" };
  if (rest === undefined || rest.length === 0) return null;
  if (head === "chat") {
    const sessionId = parseConvexIdOf<"public_chat_sessions">(rest);
    return sessionId === null ? null : { kind: "chat", sessionId };
  }
  if (head === "ticket") {
    const taskId = parseConvexIdOf<"tasks">(rest);
    return taskId === null ? null : { kind: "ticket", taskId };
  }
  if (head === "article") return { kind: "article", pageId: rest };
  return null;
}

export const MESSENGER_PARAM = "help";
